import {
  beautify,
  NoWrap,
  numberFormat,
  returnObject,
} from "../../../../utils/helpers";

type TBookingAdhocProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useBookingAdhoc = ({
  noWrapColumns = [],
  numberColumns = [],
}: TBookingAdhocProps) => {
  const tableColumns = (data: any) => {
    const firstItem = data[0] || {};

    // Define the predefined column keys to avoid duplicates
    const predefinedColumns = [
      "booking_number",
      "status",
      "booking_type",
      "city",
      "full_name",
    ];

    // Define columns to exclude from dynamic generation
    const excludedColumns = [
      "number_of_stickers",
      "sticker_numbers",
      ...predefinedColumns,
    ];

    // Custom columns for booking data with specific filters
    const filterColumns = [
      {
        id: "booking_number",
        header: <NoWrap>Booking Number</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "status",
        header: <NoWrap>Status</NoWrap>,
        filter: {
          type: "select",
          options: [
            { value: "submitted", label: "Submitted" },
            { value: "confirmed", label: "Confirmed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "completed", label: "Completed" },
          ],
        },
      },
      {
        id: "booking_type",
        header: <NoWrap>Booking Type</NoWrap>,
        filter: {
          type: "select",
          options: [
            { value: "reservation", label: "Reservation" },
            { value: "permit", label: "Permit" },
            { value: "hourly", label: "Hourly" },
          ],
        },
      },
      {
        id: "city",
        header: <NoWrap>City</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "full_name",
        header: <NoWrap>Full Name</NoWrap>,
        filter: { type: "text" },
      },
    ];

    // Generate columns from data structure, excluding predefined and excluded ones
    const dynamicColumns = Object.keys(firstItem)
      .filter((column) => !excludedColumns.includes(column))
      .map((column: any) => ({
        id: column,
        header: <NoWrap>{beautify(column)}</NoWrap>,
        filter: true,
      }));

    return [...filterColumns, ...dynamicColumns];
  };

  const tableData = (data: any) => {
    return data?.map((item: any) => ({
      ...item,
      booking_number: <NoWrap>{item.booking_number}</NoWrap>,
      status: (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.status === "submitted"
              ? "bg-blue-100 text-blue-800"
              : item.status === "confirmed"
                ? "bg-green-100 text-green-800"
                : item.status === "cancelled"
                  ? "bg-red-100 text-red-800"
                  : item.status === "completed"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-gray-100 text-gray-800"
          }`}
        >
          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
        </span>
      ),
      booking_type: (
        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
          {item.booking_type?.charAt(0).toUpperCase() +
            item.booking_type?.slice(1)}
        </span>
      ),
      city: <NoWrap>{item.city}</NoWrap>,
      full_name: <NoWrap>{item.full_name}</NoWrap>,
      phone_number: <NoWrap>{item.phone_number}</NoWrap>,
      email: <NoWrap>{item.email}</NoWrap>,
      reserved_parking_names: (
        <div className="max-w-xs">
          {item.reserved_parking_names?.length > 0 ? (
            <div className="space-y-1">
              {item.reserved_parking_names.map(
                (name: string, index: number) => (
                  <span
                    key={index}
                    className="block text-xs bg-blue-50 px-2 py-1 rounded"
                  >
                    {name}
                  </span>
                ),
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-xs">No parking assigned</span>
          )}
        </div>
      ),

      amount: item.amount ? (
        <span className="font-medium text-green-600">
          {numberFormat(item.amount)}
        </span>
      ) : (
        <span className="text-gray-400">N/A</span>
      ),
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined && item[column] !== null) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  const initialFilters = {
    booking_number: "",
    status: "",
    booking_type: "",
    city: "",
    full_name: "",
  };

  // Additional utility functions for booking analysis
  const getBookingStats = (data: any) => {
    const total = data.length;
    const submitted = data.filter(
      (item: any) => item.status === "submitted",
    ).length;
    const confirmed = data.filter(
      (item: any) => item.status === "confirmed",
    ).length;
    const cancelled = data.filter(
      (item: any) => item.status === "cancelled",
    ).length;
    const completed = data.filter(
      (item: any) => item.status === "completed",
    ).length;

    const totalSlots = data.reduce(
      (sum: number, item: any) => sum + (item.number_of_slots || 0),
      0,
    );

    const totalAmount = data.reduce(
      (sum: number, item: any) => sum + (item.amount || 0),
      0,
    );

    return {
      total,
      submitted,
      confirmed,
      cancelled,
      completed,
      totalSlots,
      totalAmount,
      confirmationRate: total > 0 ? ((confirmed / total) * 100).toFixed(1) : 0,
      cancellationRate: total > 0 ? ((cancelled / total) * 100).toFixed(1) : 0,
    };
  };

  const getCitySummary = (data: any) => {
    const cityGroups = data.reduce((acc: any, item: any) => {
      const city = item.city;
      if (!acc[city]) {
        acc[city] = {
          total: 0,
          submitted: 0,
          confirmed: 0,
          cancelled: 0,
          completed: 0,
          totalSlots: 0,
          totalAmount: 0,
        };
      }
      acc[city].total++;
      acc[city][item.status] = (acc[city][item.status] || 0) + 1;
      acc[city].totalSlots += item.number_of_slots || 0;
      acc[city].totalAmount += item.amount || 0;
      return acc;
    }, {});

    return Object.entries(cityGroups).map(([city, stats]: [string, any]) => ({
      city,
      ...stats,
      confirmationRate: ((stats.confirmed / stats.total) * 100).toFixed(1),
    }));
  };

  const getBookingTypeSummary = (data: any) => {
    const typeGroups = data.reduce((acc: any, item: any) => {
      const type = item.booking_type;
      if (!acc[type]) {
        acc[type] = {
          total: 0,
          totalSlots: 0,
          totalAmount: 0,
        };
      }
      acc[type].total++;
      acc[type].totalSlots += item.number_of_slots || 0;
      acc[type].totalAmount += item.amount || 0;
      return acc;
    }, {});

    return Object.entries(typeGroups).map(([type, stats]: [string, any]) => ({
      booking_type: type,
      ...stats,
    }));
  };

  return {
    tableColumns,
    tableData,
    initialFilters,
    getBookingStats,
    getCitySummary,
    getBookingTypeSummary,
  };
};

export default useBookingAdhoc;

// Specific implementation for booking reports
export const useBookingAdhocReport = () => {
  const noWrapColumns = [
    "booking_number",
    "full_name",
    "phone_number",
    "email",
  ];
  const numberColumns = ["number_of_slots", "duration", "amount"];

  const {
    tableColumns,
    tableData,
    initialFilters,
    getBookingStats,
    getCitySummary,
    getBookingTypeSummary,
  } = useBookingAdhoc({
    numberColumns,
    noWrapColumns,
  });

  return {
    tableColumns,
    tableData,
    initialFilters,
    getBookingStats,
    getCitySummary,
    getBookingTypeSummary,
  };
};

// Usage example with summary component
export const useBookingDashboard = () => {
  const {
    tableColumns,
    tableData,
    initialFilters,
    getBookingStats,
    getCitySummary,
    getBookingTypeSummary,
  } = useBookingAdhocReport();

  const renderBookingSummary = (data: any) => {
    const stats = getBookingStats(data);

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-blue-600">Total Bookings</h3>
          <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-yellow-600">Submitted</h3>
          <p className="text-2xl font-bold text-yellow-900">
            {stats.submitted}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-green-600">Confirmed</h3>
          <p className="text-2xl font-bold text-green-900">{stats.confirmed}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-red-600">Cancelled</h3>
          <p className="text-2xl font-bold text-red-900">{stats.cancelled}</p>
        </div>
      </div>
    );
  };

  const renderBookingMetrics = (data: any) => {
    const stats = getBookingStats(data);

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-purple-600">Total Slots</h3>
          <p className="text-2xl font-bold text-purple-900">
            {stats.totalSlots}
          </p>
        </div>
        <div className="bg-emerald-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-emerald-600">Total Amount</h3>
          <p className="text-2xl font-bold text-emerald-900">
            {stats.totalAmount > 0 ? numberFormat(stats.totalAmount) : "N/A"}
          </p>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-indigo-600">
            Confirmation Rate
          </h3>
          <p className="text-2xl font-bold text-indigo-900">
            {stats.confirmationRate}%
          </p>
        </div>
      </div>
    );
  };

  return {
    tableColumns,
    tableData,
    initialFilters,
    getBookingStats,
    getCitySummary,
    getBookingTypeSummary,
    renderBookingSummary,
    renderBookingMetrics,
  };
};
